import type {ReactNode} from "react";
import {Suspense, use, cache, useMemo} from "react";
import type {AssetEntry, AssetId, GroupId} from "../models/GroupRepo";
import {Box, Col, Text} from "../ui/Box";
import {getAssetFromLocalStore} from "./local-asset-store";
import {httpAssetHost} from "./server-hosts";

const assetCache = cache(async (id: AssetId) =>
  getAssetFromLocalStore(id).then((val) =>
    val ? URL.createObjectURL(new Blob([val.content], {type: val.mimeType})) : null
  )
);

const Fallback = ({
  children,
  meta,
  className,
}: {
  children?: ReactNode;
  meta: AssetEntry["meta"];
  className?: string;
}) => (
  <Box
    bg="surface"
    style={{width: meta.width, height: meta.height}}
    maxWidth="100%"
    className={className}
    relative
  >
    <Col absolute inset="0" align="center" justify="center">
      {children}
    </Col>
  </Box>
);

const LocalAsset = ({
  asset,
  className,
}: {
  asset: AssetEntry & {location: "local"};
  className?: string;
}) => {
  // useMemo callbacks must not be async (react/use-memo); this just hands the promise to use()
  // eslint-disable-next-line @typescript-eslint/promise-function-async
  const prom = useMemo(() => assetCache(asset.id), [asset.id]);
  const url = use(prom);
  const {width, height} = asset.meta;
  if (!url) {
    return (
      <Fallback meta={asset.meta} className={className}>
        <Text size="12" bold color="secondary">
          Not synced
        </Text>
      </Fallback>
    );
  } else {
    return <img src={url} className={className} width={width} height={height} />;
  }
};

export const ShowAsset = ({
  asset,
  groupId,
  className,
}: {
  asset: AssetEntry;
  groupId: GroupId;
  className?: string;
}) => {
  if (asset.location === "local") {
    return (
      <Suspense fallback={<Fallback meta={asset.meta} className={className} />}>
        <LocalAsset asset={asset} className={className} />
      </Suspense>
    );
  } else {
    const {width, height} = asset.meta;

    return (
      <img
        alt="Cover Image"
        src={`${httpAssetHost}/${groupId}/${asset.id}/${asset.name}`}
        width={width}
        height={height}
        crossOrigin="anonymous"
        className={className}
      />
    );
  }
};
